import { ShareButtonSecondary } from 'components/common-form/sidePanel/docToolbar/summary/ShareButtonSecondary';
import { LastUpdate } from 'components/secondaryMarket/live/sidePanel/LastUpdate';
import { ConnectedPriceAdjustment } from 'components/secondaryMarket/live/sidePanel/quoted/priceAdjustement/PriceAdjustment.connect';
import type { QuotedSidePanelProps } from 'components/secondaryMarket/live/sidePanel/quoted/QuotedSidePanel.connect';
import { ConnectedQuotedTabsPanel } from 'components/secondaryMarket/live/sidePanel/quoted/QuotedTabPanel.connect';
import { TradeWay } from 'components/secondaryMarket/shared/SidePanel/TradeWay';
import { ActionButton, ButtonPanel, Price } from 'components/secondaryMarket/shared/status';
import { Countdown } from 'components/secondaryMarket/shared/status/Countdown';
import { PanelFooter, PanelHeader } from 'components/secondaryMarket/shared/status/Panel';
import { LightAlert } from 'components/shared/LightAlert';
import { ProgressBar } from 'components/shared/ProgressBar/ProgressBar';
import { usePercentOrAmountFormatter as useFormatPercentOrAmount } from 'hooks/usePercentAmount';
import { useProgressTimer } from 'hooks/useProgressTimer';
import { FC } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';

import { FlexColumnPanel } from '../../../shared/SidePanel/FlexColumnPanel';
import { TraceReporting } from './TraceReporting';
export const QuoteEndedWarning: FC<{ className?: string }> = ({ className }) => {
  const { formatMessage } = useIntl();
  return (
    <LightAlert severity="warning" className={className}>
      {formatMessage({ id: 'secondaryMarket.sidePanel.label.quoteEnded' })}
    </LightAlert>
  );
};

export const QuotedSidePanel: FC<QuotedSidePanelProps> = ({
  formId,
  onDone,
  onTrade,
  onTraceReportingChanged,
  onReprice,
  isEnded,
  adjustedPrice,
  priceCurrency,
  priceType,
  canTrade,
  tradeAuthDurationInMillis,
  tradeAuthStartTimeInMillis,
  eligibleTraceReporting,
  traceReporting,
  accruedInterest,
  quotationType,
  cleanPrice,
  isPriceAdjusted,
  tradeWay,
  isLive,
  settlementPrice,
  trend,
  initialUpfront,
}) => {
  const { formatMessage } = useIntl();
  const formatPercentOrAmount = useFormatPercentOrAmount();

  const formattedAccruedInterest = formatPercentOrAmount({
    type: priceType,
    value: accruedInterest,
    currency: priceCurrency,
    alreadyMultiplied: true,
  });

  const { progressInPercent, remainingTimeInSeconds } = useProgressTimer(
    tradeAuthStartTimeInMillis ?? 0,
    tradeAuthDurationInMillis ?? 0,
  );
  const formattedSettlementPrice = formatPercentOrAmount({
    type: priceType,
    value: adjustedPrice,
    currency: priceCurrency,
    alreadyMultiplied: true,
  });
  const side = tradeWay === 'ClientSell' ? 'bid' : 'ask';

  return (
    <FlexColumnPanel>
      <PanelHeader color={side} discreet className="flex-shrink-0">
        <div className="d-flex justify-content-between">
          <TradeWay tradeWay={tradeWay} />
          <ShareButtonSecondary
            tabId={formId}
            color={tradeWay === 'ClientSell' ? 'secondaryquotedclientsell' : 'secondaryquotedclientbuy'}
            mustForceAdvancedMode={false}
            workspaceType="secondaryMarketQuotes"
            type="live"
          />
        </div>
        <div className="d-flex justify-content-between pb-2">
          <div className="d-flex">
            <div>
              {formatMessage({
                id:
                  quotationType === 'Clean'
                    ? 'secondaryMarket.quotePanel.resume.label.cleanPrice'
                    : 'secondaryMarket.sidePanel.label.settlementPrice',
              })}
              <Price
                dataE2e={quotationType === 'Clean' ? 'clean-price' : 'settlement-price'}
                price={quotationType === 'Clean' ? cleanPrice : adjustedPrice}
                priceType={priceType}
                priceCurrency={priceCurrency}
              />
            </div>
            {quotationType === 'Clean' && (
              <div className="d-flex align-self-end">
                {accruedInterest === undefined ? undefined : (
                  <div className="mx-5">
                    <div className="d-flex flex-grow">
                      <FormattedMessage id="secondaryMarket.sidePanel.label.accruedInterest" />
                    </div>
                    {formattedAccruedInterest}
                  </div>
                )}
                {formattedSettlementPrice === undefined ? undefined : (
                  <div>
                    <div className="d-flex flex-grow">
                      <FormattedMessage id="secondaryMarket.sidePanel.label.settlementPrice" />
                    </div>
                    {formattedSettlementPrice}
                  </div>
                )}
              </div>
            )}
          </div>
          {isEnded ? null : <Countdown remainingTimeInSeconds={remainingTimeInSeconds} />}
        </div>
      </PanelHeader>
      <div className={`progress-remaining-${side}`}>
        <ProgressBar percent={100 - progressInPercent} className={`progress-elapsed-${side}`} />
      </div>
      <PanelFooter color={side} discreet statusKey="secondaryMarket.sidePanel.label.quoted" icon="adjust" live={isLive}>
        <span className="mx-2">
          <FormattedMessage id="secondaryMarket.sidePanel.priceAdjustement.upfront" />{' '}
          <span className="tabular-figures">
            {initialUpfront === undefined ? null : (
              <FormattedNumber value={initialUpfront} minimumFractionDigits={2} maximumFractionDigits={2} />
            )}
            {priceType === 'Percent' ? '%' : priceCurrency}
          </span>
        </span>
        <span className="mx-2">
          {tradeWay === 'ClientSell' ? 'Bid' : 'Ask'}{' '}
          <span className="tabular-figures">
            {settlementPrice === undefined ? null : (
              <FormattedNumber value={settlementPrice} minimumFractionDigits={2} maximumFractionDigits={2} />
            )}
            {priceType === 'Percent' ? '%' : priceCurrency}
          </span>
          <i className="ms-1 icon icon-sm">
            {trend === 'increase'
              ? 'trending_up'
              : trend === 'decrease'
                ? 'trending_down'
                : /* this is to make sure we keep the space when the price doesn't change */ 's'}
          </i>
        </span>
        <LastUpdate tabId={formId} />
      </PanelFooter>
      <ConnectedPriceAdjustment formId={formId} />
      {isPriceAdjusted && (
        <div className="alert alert-discreet-warning p-2 mb-3">
          <i className="icon icon-sm me-2 mb-auto">warning</i>
          <FormattedMessage id="secondaryMarket.sidePanel.label.settlementPriceAdjusted" />
        </div>
      )}
      {isEnded && <QuoteEndedWarning className="mb-3" />}
      {eligibleTraceReporting ? (
        <TraceReporting value={traceReporting} onValueChanged={onTraceReportingChanged} />
      ) : (
        <div className="py-3" />
      )}
      <ConnectedQuotedTabsPanel formId={formId} />
      <ButtonPanel>
        <ActionButton
          color="info"
          label="secondaryMarket.sidePanel.cta.confirmToTrade"
          disabled={!canTrade}
          onClick={onTrade}
        />
        <div className="d-flex">
          <ActionButton
            color="discreet-secondary"
            className="col"
            label="secondaryMarket.sidePanel.cta.cancel"
            disabled={false}
            onClick={() => onDone(undefined, true)}
          />
          <ActionButton
            color="default"
            className="col"
            label="secondaryMarket.sidePanel.cta.reprice"
            disabled={false}
            onClick={onReprice}
            icon="refresh"
          />
        </div>
      </ButtonPanel>
    </FlexColumnPanel>
  );
};
QuotedSidePanel.displayName = 'QuotedSidePanel';
